import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import _ from "lodash";
import { ChevronLeftIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useActivePanelNode } from "@/app/app/_/layouts/ActivePanelNodeContext";
import { panelOverridesBreadcrumbDetail } from "@/app/app/_/layouts/nodePanelConfig";
import { useAppRouteNavigation, useAppRouteState } from "@/app/app/_/routing";
import { getReportsTabKey } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import type { IObjectWithTypename } from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { NodeTitleEditable } from "@/components/organisms/Node/NodeTitleLayout";
import { NodeTitleLayoutEntityBox } from "@/components/organisms/Node/NodeTitleLayoutEntityBox";
import { UtilityBarSearch } from "@/components/organisms/UtilityBar/UtilityBarSearch";
import { Navigation, Queries, Values } from "@/constants";
import {
  CONNECTION_VIEWS,
  type ConnectionViewConfig,
  type ConnectionViewType,
  isConnectionView,
  useConnectionSetup,
} from "@/hooks/useConnectionSetup";
import { useDevice } from "@/hooks/useDevice";
import { Objects } from "@/objects";
import { icons } from "@/objects/@icons";
import { idToIdentifier, idToTypename } from "@/objects/@utils";
import { State } from "@/state";
import type { AppViewTabModeType } from "@/state/@types";
import { defaultTabLabel, threadNodeIdToTabId } from "@/state/threadTabs";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

function getViewTitle(viewKey: string): string {
  const item = Navigation.SIDEBAR.find(
    (entry) => entry.type === "routing" && entry.key === viewKey,
  );
  if (item && "name" in item) {
    return item.name as string;
  }
  return _.capitalize(viewKey.replace(/-/g, " "));
}

function formatTabLabel(tab: string | null | undefined): string {
  if (!tab) return "";
  return _.capitalize(String(tab).replace(/-/g, " "));
}

function BreadcrumbDetailTitle({
  fallbackTitle,
  usePanelStore = true,
}: {
  fallbackTitle: string;
  /** When false, render the breadcrumb title statically instead of the panel store. */
  usePanelStore?: boolean;
}) {
  const panelNode = useActivePanelNode();

  if (usePanelStore && panelNode?.store) {
    // The editable title renders an ``input``, which does not inherit
    // ``text-decoration`` from this wrapper — underline it directly.
    return (
      <div className="[&_input]:underline">
        <NodeTitleEditable
          store={panelNode.store}
          isLoading={panelNode.isLoading}
          fallbackTitle={fallbackTitle}
          size="sm"
          layout="content"
        />
      </div>
    );
  }

  return (
    <Typography
      level="body-md"
      className="truncate pl-1 pr-1 min-w-0 overflow-hidden text-ellipsis [text-decoration:underline]"
    >
      {fallbackTitle}
    </Typography>
  );
}

/**
 * Previous / next arrows on a routed detail page, stepping through the list the
 * record was opened from.
 *
 * That list is still described by the URL — its tab, filters and sort survive
 * the move to ``/app/documents/all/<identifier>`` — so the same connection
 * query resolves from the cache the list left behind, in the order the list
 * showed it. A record that is not in that list (opened from a reference in
 * another view) gets no arrows.
 */
function BreadcrumbDetailPager({
  appView,
  detailIdentifier,
}: {
  appView: ConnectionViewType;
  detailIdentifier: string;
}) {
  const appRouteNavigation = useAppRouteNavigation();
  const { rows, totalCount, loadNext, loading } = useConnectionSetup<{
    id: string;
  }>({ appView, minLoadingMs: 0 });

  const index = rows.findIndex(
    (row) => idToIdentifier(row.id) === detailIdentifier,
  );
  const previousRow = index > 0 ? rows[index - 1] : null;
  const nextRow = index >= 0 ? (rows[index + 1] ?? null) : null;
  const isOnLastLoadedRow =
    index >= 0 && index === rows.length - 1 && rows.length < totalCount;

  // Fetch the next page on landing on the last loaded row, so "next" is ready
  // by the time it is clicked. Keyed on the flag alone: once per arrival.
  useEffect(() => {
    if (isOnLastLoadedRow) {
      void loadNext();
    }
  }, [isOnLastLoadedRow]);

  // Preload both neighbours, so a step lands on a record already in the cache:
  // the same query and variables ``NodeDetailLayout`` reads cache-first.
  const { startDate, endDate } = State.getState().timeSeriesFilter;
  useQuery(Queries.NODE_QUERY_WITH_PENDING_CHANGE, {
    variables: {
      id: previousRow?.id ?? "",
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    skip: !previousRow,
  });
  useQuery(Queries.NODE_QUERY_WITH_PENDING_CHANGE, {
    variables: {
      id: nextRow?.id ?? "",
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    skip: !nextRow,
  });

  // Held (disabled) while the list resolves, so the title does not jump when
  // the arrows arrive; dropped only once the list is known not to hold it.
  if (index < 0 && !loading) {
    return null;
  }

  return (
    <div className="flex items-center shrink-0">
      {(
        [
          ["Previous", icons.chevronLeft, previousRow],
          ["Next", icons.chevronRight, nextRow],
        ] as const
      ).map(([label, ChevronIcon, row]) => (
        <Tooltip key={label}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={label}
              disabled={!row}
              // Replace rather than push: Back leaves for the list, like the
              // breadcrumb and Escape, instead of retracing every step.
              onClick={() => {
                if (!row) return;
                // The table cell this page was opened from follows the step, so
                // returning to the list focuses the row you ended up on.
                const { detailOriginCell } = State.getState();
                if (detailOriginCell) {
                  State.setState({
                    detailOriginCell: { ...detailOriginCell, rowId: row.id },
                  });
                }
                appRouteNavigation.navigateTo(
                  { detailIdentifier: idToIdentifier(row.id) },
                  { replace: true },
                );
              }}
            >
              <ChevronIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

export function BreadcrumbSeparator() {
  return (
    <Typography
      level="body-sm"
      className="text-muted-foreground/80 ml-0.5 mr-0.5 [user-select:none] shrink-0"
    >
      /
    </Typography>
  );
}

type BreadcrumbOption = { id: string; label: string };

/**
 * One crumb in a top-bar trail. Every crumb is plain ``body-md`` in the same
 * color, so the trail reads as a path rather than a fade; an underline marks
 * the crumb you are on and appears on hover for the ones you can go back to.
 * With ``options`` a chevron opens a picker for the crumb's siblings.
 */
export function BreadcrumbCrumb({
  label,
  isSelected,
  onClick,
  options,
  onSelectOption,
}: {
  label: string;
  isSelected: boolean;
  onClick?: () => void;
  options?: BreadcrumbOption[];
  onSelectOption?: (option: BreadcrumbOption) => void;
}) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const crumb = onClick ? (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      // ``body-md`` carries no ``fontWeight``, so a Button would fall back
      // to its own. Pin it to what <Typography level="body-md"> renders.
      className={cn(
        "font-medium text-sm leading-snug text-foreground pl-1 pr-1 min-h-7 shrink-0 [box-shadow:none]",
        isSelected
          ? "underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial]"
          : "no-underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial]",
        "hover:underline hover:[text-decoration-thickness:initial] hover:[text-decoration-style:initial] hover:decoration-[initial]",
      )}
    >
      {label}
    </Button>
  ) : (
    <Typography
      level="body-md"
      className={cn(
        "truncate",
        "pl-1 pr-1 shrink-0 text-foreground",
        isSelected
          ? "underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial]"
          : "no-underline [text-decoration-thickness:initial] [text-decoration-style:initial] decoration-[initial]",
      )}
    >
      {label}
    </Typography>
  );

  if (!options || options.length <= 1) {
    return crumb;
  }

  return (
    <div className="flex items-center shrink-0">
      {crumb}
      <Popper
        isOpenState={[isPickerOpen, setIsPickerOpen]}
        // A bar's, as the steps to the rows beside it are.
        anchor={
          <Button variant="ghost" size="icon-sm" className="-ml-1">
            <icons.chevronDown />
          </Button>
        }
      >
        <List
          data={options}
          getRowKey={(item: BreadcrumbOption) => item.id}
          onItemClick={(item) => {
            onSelectOption?.(item);
            setIsPickerOpen(false);
          }}
          className="min-w-50"
        >
          <List.Items>
            {(item: BreadcrumbOption) => (
              <List.Item value={item}>
                <Typography level="body-md">{item.label}</Typography>
              </List.Item>
            )}
          </List.Items>
        </List>
      </Popper>
    </div>
  );
}

export function AppBreadcrumbs() {
  const { isNarrow } = useDevice();
  const activeAppView = State((store) => store.activeAppView);
  const activeTab = State((store) => store.activeTab);
  const activeTabNodeIds = State((store) => store.activeTabNodeIds);
  const activePanelNodeId = State((store) => store.activePanelNodeId);
  const setActiveTabNodeIds = State((store) => store.setActiveTabNodeIds);
  const activeThreadNodeId = State((store) => store.activeThreadNodeId);
  const openThreadTabs = State((store) => store.openThreadTabs);
  const appRouteNavigation = useAppRouteNavigation();
  const routeState = useAppRouteState();
  const { pathname } = useLocation();

  const tabKey =
    activeAppView === "reports" ? getReportsTabKey(activeTab) : activeTab;
  const tabValues = (Values.APP_VIEW_TAB_MODE_MAP[activeAppView]?.values ??
    []) as AppViewTabModeType[];
  const hasTabs = tabValues.length > 0;

  const activeTabNodeId = (
    activeTabNodeIds as Record<
      string,
      Record<string, string | null> | undefined
    >
  )?.[activeAppView]?.[tabKey];
  const reportsDetailNodeId =
    activeAppView === "reports" && routeState.viewId ? routeState.viewId : null;
  const detailNodeId = activeTabNodeId ?? reportsDetailNodeId;
  const activeTabNodeType = idToTypename(detailNodeId);
  const isThreadDetailView =
    activeAppView === "thread" && activeThreadNodeId != null;
  const isNodeDetailView = Boolean(detailNodeId);
  const isDetailView = isNodeDetailView || isThreadDetailView;
  // The last crumb is the one you are on; it carries the underline.
  const isViewCrumbSelected = !hasTabs && !isDetailView;
  // A list backed by a searchable model is searched from here, after the
  // crumb it is: the search is the list's, so a record's page has none. Read
  // from the route, as the page drops the list's chips by it; the store lags
  // it by a render.
  const isSearchable =
    routeState.detailIdentifier == null &&
    isConnectionView(routeState.view) &&
    (CONNECTION_VIEWS[routeState.view] as ConnectionViewConfig).searchable ===
      true;
  const panelShowsNestedDetail = panelOverridesBreadcrumbDetail(
    detailNodeId,
    activePanelNodeId,
  );

  const panelNode = useActivePanelNode();
  const { data: activeTabNodeQuery } = useQuery(Queries.NODE_QUERY, {
    variables: { id: detailNodeId },
    skip: !detailNodeId,
  });
  const { data: activeThreadNodeQuery } = useQuery(Queries.NODE_QUERY, {
    variables: { id: activeThreadNodeId! },
    skip: !activeThreadNodeId || activeAppView !== "thread",
  });

  const detailTitle = (() => {
    if (isThreadDetailView) {
      const tabId = threadNodeIdToTabId(activeThreadNodeId);
      const tabLabel = openThreadTabs.find((t) => t.id === tabId)?.label;
      const defaultLabel = defaultTabLabel(tabId);
      if (tabLabel && tabLabel !== defaultLabel) {
        return tabLabel;
      }
      const fromNode = Objects.ThreadObject.getTitle(
        activeThreadNodeQuery?.node as Parameters<
          typeof Objects.ThreadObject.getTitle
        >[0],
      );
      if (fromNode) return fromNode;
      return tabLabel ?? defaultLabel;
    }
    if (!detailNodeId) return null;
    if (
      panelShowsNestedDetail &&
      activeTabNodeType &&
      Objects[activeTabNodeType]
    ) {
      const detailTitle = Objects[activeTabNodeType].getTitle(
        activeTabNodeQuery?.node as IObjectWithTypename,
      );
      if (detailTitle) return detailTitle;
    }
    if (!panelShowsNestedDetail && panelNode?.title) return panelNode.title;
    if (activeTabNodeType && Objects[activeTabNodeType]) {
      return Objects[activeTabNodeType].getTitle(
        activeTabNodeQuery?.node as IObjectWithTypename,
      );
    }
    return null;
  })();

  const navigateToListRoot = () => {
    if (activeAppView === "reports") {
      appRouteNavigation.navigateToView("reports", {
        viewId: null,
        preservePanel: true,
        preserveQuery: true,
      });
      return;
    }

    if (activeAppView === "thread") {
      State.setState({ activeThreadNodeId: null });
      return;
    }

    const currentIds = State.getState().activeTabNodeIds as Record<
      string,
      Record<string, string | null>
    >;
    setActiveTabNodeIds({
      ...currentIds,
      [activeAppView]: {
        ...((currentIds?.[activeAppView] || {}) as Record<
          string,
          string | null
        >),
        [tabKey]: null,
      },
    });
  };

  const navigateToTabList = () => {
    if (isDetailView) {
      navigateToListRoot();
    }
  };

  const handleTabChange = (tab: AppViewTabModeType) => {
    appRouteNavigation.navigateToView(activeAppView, {
      tab,
      ruleId: null,
      viewId: null,
      preservePanel: true,
      preserveQuery: false,
    });
  };

  const viewTitle = getViewTitle(activeAppView);
  const effectiveTab =
    activeTab && tabValues.includes(activeTab as AppViewTabModeType)
      ? activeTab
      : tabValues[0];
  const tabLabel = formatTabLabel(effectiveTab);

  const tabOptions = useMemo(
    () =>
      tabValues.map((tab) => ({
        id: tab,
        label: formatTabLabel(tab),
      })),
    [tabValues],
  );

  // A page that is none of the views is named for itself, not for the view
  // last open: there, the views' state is left as it was.
  const pageName = Navigation.PAGES_OF_THEIR_OWN[pathname];
  if (pageName) {
    return isNarrow ? null : (
      <div className="flex items-center min-w-0 max-w-full overflow-hidden gap-0">
        <BreadcrumbCrumb label={pageName} isSelected={true} />
      </div>
    );
  }

  // A phone - or a window as narrow - has no room for the trail in its top
  // bar, where the businesses lead. A record's page there - a report, a rule, a document - is a screen
  // pushed from its list, and says so as a phone's own apps do: a bar of its
  // own under the top bar, the way back to the list at its start, then the
  // business's box and the record's title, still renamed in place, as a
  // desktop has them. A list's own page needs none: the tab bar says where it is.
  if (isNarrow) {
    if (!isNodeDetailView || !detailTitle) return null;
    const store = panelShowsNestedDetail ? null : panelNode?.store;
    return (
      <div
        data-testid="app-record-title-bar"
        className="flex items-center gap-1 shrink-0 min-h-12 pl-1 pr-3 pt-1 pb-1 box-border [border-bottom-width:1px] [border-bottom-style:solid] border-border bg-background"
      >
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Back to ${viewTitle}`}
          data-testid="app-record-title-back"
          onClick={navigateToListRoot}
          className="shrink-0"
        >
          <ChevronLeftIcon />
        </Button>
        {/* The business's box and the title beside it, where a desktop's
            record has them. */}
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {store ? (
            <>
              <NodeTitleLayoutEntityBox
                store={store}
                isLoading={panelNode!.isLoading}
              />
              <div className="min-w-0 flex-1">
                <NodeTitleEditable
                  store={store}
                  isLoading={panelNode!.isLoading}
                  fallbackTitle={detailTitle}
                  titleLevel="title-md"
                />
              </div>
            </>
          ) : (
            <Typography level="title-md" className="truncate">
              {detailTitle}
            </Typography>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center min-w-0 max-w-full overflow-hidden gap-0">
      {/* Every crumb is plain ``body-md`` in the same color — the trail reads
          as a path, not a fade. An underline marks the crumb you are on, and
          appears on hover for the ones you can go back to. */}
      <BreadcrumbCrumb
        label={viewTitle}
        isSelected={isViewCrumbSelected}
        onClick={navigateToListRoot}
      />

      {hasTabs && tabLabel && (
        <>
          <BreadcrumbSeparator />
          <BreadcrumbCrumb
            label={tabLabel}
            isSelected={!isDetailView}
            onClick={tabValues.length === 1 ? undefined : navigateToTabList}
            options={tabOptions}
            onSelectOption={(option) =>
              handleTabChange(option.id as AppViewTabModeType)
            }
          />
        </>
      )}

      {isDetailView && detailTitle && (
        <>
          <BreadcrumbSeparator />
          {routeState.detailIdentifier && isConnectionView(routeState.view) && (
            <BreadcrumbDetailPager
              // The hook's columns are fixed at mount; a different view
              // needs a fresh one.
              key={routeState.view}
              appView={routeState.view}
              detailIdentifier={routeState.detailIdentifier}
            />
          )}
          <div className="min-w-0 flex-[1_1_auto] overflow-hidden">
            {isThreadDetailView ? (
              <Typography
                level="body-md"
                title={detailTitle}
                className="truncate pl-1 pr-1 min-w-0 overflow-hidden text-ellipsis [text-decoration:underline]"
              >
                {detailTitle}
              </Typography>
            ) : (
              <BreadcrumbDetailTitle
                fallbackTitle={detailTitle}
                usePanelStore={!panelShowsNestedDetail}
              />
            )}
          </div>
        </>
      )}

      {isSearchable && (
        // Gives way when the bar runs out of room, so the search's own width
        // is capped by it rather than cut off.
        <div className="flex min-w-0 ml-2">
          <UtilityBarSearch />
        </div>
      )}
    </div>
  );
}
